import { AxiosResponse, AxiosError } from "axios";

import DataProvider from "@/utils/helpers/DataProvider";
import { MutationTree, ActionTree, Module, GetterTree } from "vuex";
import { State as RootState } from "@/store";
import { IUserLanguageData } from "@/models/interfaces/session";
import { UserLanguage } from "@/models/types/session";

export interface State {
  userId: string;
  userAccessToken: string;
  languageData?: IUserLanguageData;
}

export const mutations: MutationTree<State> = {
  setUserId(state, userId: string) {
    state.userId = userId;
  },

  setUserAccessToken(state, accessToken: string) {
    state.userAccessToken = accessToken;
  },

  setUserLanguages(state, data?: IUserLanguageData) {
    state.languageData = data;
  }
};

export const actions: ActionTree<State, RootState> = {
  async fetchUser() {
    return DataProvider.get("users/current")
      .then((response: AxiosResponse) => {
        return response.data;
      })
      .catch((error: AxiosError) => {
        return error;
      });
  },

  async fetchUserLanguages({ state, commit }) {
    commit("setUserLanguages");

    try {
      const { data } = await DataProvider.get<IUserLanguageData>(
        `users/${state.userId}/languages`
      );
      commit("setUserLanguages", data);
      return data;
    } catch (error) {
      commit("setUserLanguages", error);
      return error;
    }
  },

  async changeUserLanguage({ state }, locale: UserLanguage) {
    try {
      const { data } = await DataProvider.post(
        `users/${state.userId}/languages`,
        {
          supportLanguage: locale,
          teachingLanguage: "gb"
        }
      );
      return data;
    } catch (error) {
      return error;
    }
  },

  async userLogout() {
    const defaultLogout = (url: string) => {
      localStorage.removeItem("token");
      DataProvider.setDefaultHeader("Authorization", "");

      window.location.replace(url);
    };

    try {
      const {
        data: { logoutUrl }
      } = await DataProvider.get("logout");

      defaultLogout(logoutUrl);
    } catch (error) {
      defaultLogout(process.env.VUE_APP_LOGOUT_REDIRECT_URL as string);
    }
  }
};

export const getters: GetterTree<State, RootState> = {
  userLanguage(state) {
    return () => state.languageData?.supportLanguage;
  }
};

const store: Module<State, RootState> = {
  namespaced: true,
  state: {
    userId: "",
    userAccessToken: ""
  },
  getters,
  mutations,
  actions
};

export default store;
